import React, { useRef, useState } from "react";
import toast from "react-hot-toast";

import { ApiError, eventsApi } from "../../api";
import { useSubmitLock } from "../../hooks/useSubmitLock";
import { artistDisplayName } from "../../utils/artistProfile";
import { isPastDateKey } from "../../utils/calendarDates";
import { EVENTS_CSV_TEMPLATE, parseEventsCsv, type ParsedCsvEventRow } from "../../utils/eventsCsvImport";
import type { Artist, Venue } from "../../types";
import "./EventsCsvImportModal.css";

type Props = {
  open: boolean;
  onClose: () => void;
  token: string;
  artists: Artist[];
  venues: Venue[];
  onImported: () => Promise<void>;
};

export const EventsCsvImportModal: React.FC<Props> = ({ open, onClose, token, artists, venues, onImported }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedCsvEventRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const { run } = useSubmitLock();

  if (!open) return null;

  const reset = () => {
    setRows([]);
    setErrors([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    const result = parseEventsCsv(text);
    setRows(result.rows);
    setErrors(result.errors);
  };

  /** A venue is only chosen when it is unambiguous; guessing could double-book or misplace an event. */
  const resolveVenue = (city: string): { venue: Venue } | { error: string } => {
    const normalized = city.trim().toLowerCase();
    const byCity = venues.filter((venue) => (venue.city ?? "").trim().toLowerCase() === normalized);
    const candidates = byCity.length
      ? byCity
      : venues.filter((venue) => normalized && venue.location?.toLowerCase().includes(normalized));
    if (candidates.length === 1) return { venue: candidates[0] };
    if (candidates.length > 1) return { error: `${candidates.length} venues match ${city}; rename the city to pick one` };
    return { error: `no venue found for ${city}` };
  };

  const resolveArtist = (name?: string): Artist | null => {
    if (!name?.trim()) return null;
    const normalized = name.trim().toLowerCase();
    return (
      artists.find(
        (artist) => artist.name.toLowerCase() === normalized || artist.stage_name?.trim().toLowerCase() === normalized,
      ) ?? null
    );
  };

  const runImport = async () => {
    await run(async () => {
      if (rows.length === 0) {
        toast.error("Parse a CSV file before importing.");
        return;
      }
      setImporting(true);
      const failures: string[] = [];
      let pending: { csvRow: ParsedCsvEventRow; payload: Record<string, unknown> }[] = [];

      for (const row of rows) {
        if (isPastDateKey(row.event_date)) {
          failures.push(`${row.name}: date is in the past`);
          continue;
        }
        const resolved = resolveVenue(row.city);
        if ("error" in resolved) {
          failures.push(`${row.name}: ${resolved.error}`);
          continue;
        }
        const { venue } = resolved;
        const city = (venue.city ?? row.city).trim();
        if (!city) {
          failures.push(`${row.name}: venue has no city`);
          continue;
        }
        const matchedArtist = resolveArtist(row.artist);
        pending.push({
          csvRow: row,
          payload: {
            name: row.name,
            artist: matchedArtist ? artistDisplayName(matchedArtist) : (row.artist ?? null),
            artist_id: matchedArtist?.id ?? null,
            city,
            venue_capacity: row.venue_capacity,
            event_date: row.event_date,
            start_date: `${row.event_date}T18:00:00`,
            end_date: `${row.event_date}T23:00:00`,
            venue_id: venue.id,
            status: row.status,
            ticket_tiers: [{ tier_name: "General Admission", price: 0, quantity: row.venue_capacity }],
            finances: { production_cost: 0, marketing_cost: 0, artist_fee: 0 },
          },
        });
      }

      // The server imports atomically: if any row is rejected (venue clash,
      // capacity, ...) nothing is saved and it reports every failing row. Drop
      // those rows and send the rest once more so good rows still go in.
      let created = 0;
      try {
        for (let attempt = 0; attempt < 2 && pending.length > 0; attempt += 1) {
          const batch = pending;
          try {
            const result = await eventsApi.importRows(batch.map((p) => p.payload), token);
            created += result.created;
            pending = [];
          } catch (err) {
            const failed = err instanceof ApiError ? (err.detail as { failed?: { row: number; error: string }[] })?.failed : undefined;
            if (!Array.isArray(failed) || failed.length === 0) throw err;
            const bad = new Set(failed.map((f) => f.row));
            failed.forEach((f) => failures.push(`${batch[f.row - 1]?.csvRow.name ?? `Row ${f.row}`}: ${f.error}`));
            pending = batch.filter((_, index) => !bad.has(index + 1));
          }
        }
      } catch (err) {
        failures.push(err instanceof Error ? err.message : "Import failed");
      }

      setImporting(false);
      if (created > 0) {
        toast.success(`Imported ${created} event${created === 1 ? "" : "s"}`);
        await onImported();
      }
      if (failures.length > 0) {
        toast.error(failures.slice(0, 3).join(" · "));
        setErrors(failures);
      }
      if (created > 0 && failures.length === 0) handleClose();
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([EVENTS_CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "zoniq-events-import-template.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="events-csv-import" role="presentation" onMouseDown={handleClose}>
      <div className="events-csv-import__dialog eventos-card" role="dialog" aria-modal="true" aria-label="Import events from CSV" onMouseDown={(e) => e.stopPropagation()}>
        <header className="events-csv-import__head">
          <div>
            <h2 className="events-csv-import__title">Import events from CSV</h2>
            <p className="eventos-subtle">Columns: name, event_date, city, venue_capacity, artist (optional), status (optional)</p>
          </div>
          <button type="button" className="eventos-nav-btn" onClick={handleClose} disabled={importing}>
            Close
          </button>
        </header>
        <div className="events-csv-import__actions">
          <button type="button" className="eventos-nav-btn" onClick={downloadTemplate}>
            Download template
          </button>
          <label className="eventos-nav-btn events-csv-import__file-label">
            Choose CSV
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
        </div>
        {errors.length > 0 ? (
          <ul className="events-csv-import__errors">
            {errors.slice(0, 8).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        ) : null}
        {rows.length > 0 ? (
          <div className="events-csv-import__preview">
            <div className="eventos-label">{rows.length} row{rows.length === 1 ? "" : "s"} ready</div>
            <ul className="events-csv-import__rows">
              {rows.slice(0, 6).map((row) => (
                <li key={`${row.name}-${row.event_date}`}>
                  <strong>{row.name}</strong>
                  <span className="eventos-subtle">
                    {row.event_date} · {row.city} · {row.venue_capacity.toLocaleString("en-IN")} cap
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="app-form-actions">
          <button type="button" className="eventos-primary-btn" onClick={() => void runImport()} disabled={importing || rows.length === 0}>
            {importing ? "Importing…" : `Import ${rows.length || ""} event${rows.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
};
